"use client";

import { useEffect, useMemo, useRef, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  ruleForCarrier,
  validateWritingNumber,
  sanitizeWritingNumberInput,
} from "../../writingNumberRules";
import {
  AlertCircle,
  Building2,
  CalendarDays,
  Check,
  Clipboard,
  Layers,
  Loader2,
  MapPin,
  Plus,
  RefreshCw,
  Shield,
  ShieldCheck,
  Trash2,
  User as UserIcon,
} from "lucide-react";

import { Button } from "@/app/_module/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/app/_module/components/ui/dialog";
import { Input } from "@/app/_module/components/ui/input";

import {
  techficientService,
  type ProducerProfileResponse,
  type TechficientProduct,
  type TechficientEligibility,
} from "@/app/_module/services/techficientService";

const US_STATES = [
  "AL", "AK", "AZ", "AR", "CA", "CO", "CT", "DE", "FL", "GA",
  "HI", "ID", "IL", "IN", "IA", "KS", "KY", "LA", "ME", "MD",
  "MA", "MI", "MN", "MS", "MO", "MT", "NE", "NV", "NH", "NJ",
  "NM", "NY", "NC", "ND", "OH", "OK", "OR", "PA", "RI", "SC",
  "SD", "TN", "TX", "UT", "VT", "VA", "WA", "WV", "WI", "WY",
];

interface ManageProfileProps {
  /** Pre-loaded eligibility (so we don't refetch on mount). */
  eligibility: TechficientEligibility;
  /** Optional close handle when rendered in a dialog. */
  onClose?: () => void;
  /** Open scrolled to State Licenses with a row for this state ready to fill
   *  (Apply bounced on a missing license). */
  focusStateCode?: string;
  /** Fires after a save that actually reached the carrier profile. */
  onSaved?: () => void;
}

/**
 * Manage Profile — the post-onboarding dashboard for editing the agent's
 * producer profile. Different from the wizard intentionally: it's a single
 * scrollable surface (not a stepper) so an agent can hop between sections,
 * edit anything, hit Save once.
 *
 * Three sections:
 *   1. KPI strip — state licenses count, carrier appointments count, last synced
 *   2. User Information — name, contact, DOB, NPN, SSN (masked), address block
 *   3. State Licenses — list with add/remove (digits-only)
 *   4. Carrier Appointments — refresh + paste-bulk + per-carrier rows
 *
 * Save is a single call: PATCHes user info via `/techficient/profile`, then
 * if licenses or contracts changed, POSTs the wizard's `/techficient/onboard`
 * endpoint (idempotent — find-or-update on Techficient's side).
 */
export function ManageProfile({
  eligibility,
  onClose,
  focusStateCode,
  onSaved,
}: ManageProfileProps) {
  const queryClient = useQueryClient();

  // ── load the user's editable profile ──
  const { data: profile, isLoading: profileLoading } = useQuery({
    queryKey: ["techficient", "profile"],
    queryFn: () => techficientService.getProducerProfile(),
    staleTime: 30_000,
  });

  // ── load carriers from Techficient (same as the wizard) ──
  const [forceRefresh, setForceRefresh] = useState(false);
  const {
    data: products = [],
    isLoading: productsLoading,
    isFetching: productsRefreshing,
    refetch: refetchProducts,
  } = useQuery({
    queryKey: ["techficient", "products", "all", forceRefresh],
    queryFn: () => techficientService.listProducts(false, forceRefresh),
    staleTime: 5 * 60_000,
  });
  const carriers = useMemo(() => {
    const m = new Map<string, { carrierCode: string; carrierName: string }>();
    for (const p of products as TechficientProduct[]) {
      if (!m.has(p.carrierCode)) {
        m.set(p.carrierCode, {
          carrierCode: p.carrierCode,
          carrierName: p.carrierName,
        });
      }
    }
    return Array.from(m.values()).sort((a, b) =>
      a.carrierName.localeCompare(b.carrierName),
    );
  }, [products]);

  // ── editable state ──
  const [form, setForm] = useState<EditableProfile>(emptyForm());
  const [ssnInput, setSsnInput] = useState("");
  const [licenses, setLicenses] = useState<
    Array<{ stateCode: string; licenseNumber: string }>
  >([]);
  const [contracts, setContracts] = useState<Record<string, string>>({});
  const [bulkOpen, setBulkOpen] = useState(false);
  const [bulkText, setBulkText] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [savedFlash, setSavedFlash] = useState(false);

  // ── seed editable state from server data ──
  useEffect(() => {
    if (profile) setForm(profileToEditable(profile));
  }, [profile]);
  useEffect(() => {
    const seeded = eligibility?.stateLicenses?.length
      ? [...eligibility.stateLicenses]
      : [];
    const focus = focusStateCode?.toUpperCase();
    if (focus && !seeded.some((l) => l.stateCode === focus)) {
      seeded.push({ stateCode: focus, licenseNumber: "" });
    }
    setLicenses(seeded.length ? seeded : [{ stateCode: "", licenseNumber: "" }]);
    const seedC: Record<string, string> = {};
    for (const c of eligibility?.carrierContracts || []) {
      if (c.carrierCode) seedC[c.carrierCode] = c.writingNumber || "";
    }
    setContracts(seedC);
  }, [eligibility, focusStateCode]);

  // Jump to the licence row the agent came here for.
  const licensesRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!focusStateCode) return;
    const t = setTimeout(() => {
      const box = licensesRef.current;
      if (!box) return;
      box.scrollIntoView({ behavior: "smooth", block: "center" });
      box
        .querySelector<HTMLInputElement>(
          `input[data-state="${focusStateCode.toUpperCase()}"]`,
        )
        ?.focus({ preventScroll: true });
    }, 150);
    return () => clearTimeout(t);
  }, [focusStateCode, profileLoading]);

  // ── derived ──
  const lastSyncedDisplay = useMemo(() => {
    if (!eligibility.lastSyncedAt) return "Never";
    const d = new Date(eligibility.lastSyncedAt);
    if (Number.isNaN(d.getTime())) return "Never";
    return d.toLocaleDateString(undefined, {
      month: "2-digit",
      day: "2-digit",
      year: "numeric",
    });
  }, [eligibility.lastSyncedAt]);

  const cleanLicenses = licenses.filter(
    (l) => l.stateCode && l.licenseNumber.trim(),
  );
  const filledContracts = Object.entries(contracts).filter(
    ([, v]) => v && v.trim(),
  );

  // ── save ──
  const save = async () => {
    // Licences only reach the carrier profile with an NPN — without this the
    // save "succeeds" and the licence silently goes nowhere.
    if (cleanLicenses.length && !form.npn.replace(/\D+/g, "")) {
      setError("Add your NPN first — carriers need it to save state licenses.");
      return;
    }
    setSaving(true);
    setError(null);
    try {
      // 1. Update editable user fields (name / contact / DOB / address / NPN / SSN if changed).
      const patch: Record<string, any> = {
        firstName: form.firstName.trim(),
        lastName: form.lastName.trim(),
        phone: form.phone.replace(/\D+/g, "") || undefined,
        npn: form.npn.replace(/\D+/g, "") || undefined,
        dob: form.dob || undefined,
        address: form.address.trim() || undefined,
        city: form.city.trim() || undefined,
        state: form.state || undefined,
        zip: form.zip.replace(/\D+/g, "") || undefined,
      };
      if (ssnInput) patch.ssn = ssnInput.replace(/\D+/g, "");
      await techficientService.updateProducerProfile(patch);

      // 2. Push the licenses + carrier writing numbers via the existing
      // onboarding endpoint (idempotent — find-and-update on the Techficient
      // side, so it doubles as a "save changes" path).
      if (form.npn.trim() && cleanLicenses.length) {
        await techficientService.onboard({
          npn: form.npn.replace(/\D+/g, ""),
          stateLicenses: cleanLicenses,
          carrierContracts: filledContracts.map(([carrierCode, writingNumber]) => ({
            carrierCode,
            writingNumber: writingNumber.trim(),
          })),
        });
      }

      // 3. Cache busts so the parent page re-renders with fresh data.
      queryClient.invalidateQueries({ queryKey: ["techficient", "eligibility"] });
      queryClient.invalidateQueries({ queryKey: ["techficient", "profile"] });
      setSsnInput("");
      setSavedFlash(true);
      setTimeout(() => setSavedFlash(false), 1800);
      onSaved?.();
    } catch (e: any) {
      const raw =
        e?.response?.data?.message ||
        e?.message ||
        "Couldn't save — check the fields and try again.";
      // White-label: scrub vendor mentions from surfaced error text.
      setError(String(raw).replace(/techficient|techf\.com/gi, "carrier"));
    } finally {
      setSaving(false);
    }
  };

  // ── carrier paste-bulk ──
  const applyBulk = () => {
    const next: Record<string, string> = { ...contracts };
    const valid = new Set(carriers.map((c) => c.carrierCode));
    let applied = 0;
    let skipped = 0;
    for (const raw of bulkText.split(/\r?\n/)) {
      const line = raw.trim();
      if (!line) continue;
      const parts = line.split(/[,\t]/).map((p) => p.trim());
      if (parts.length < 2) {
        skipped++;
        continue;
      }
      const [code, num] = parts;
      if (!valid.has(code)) {
        skipped++;
        continue;
      }
      const clean = (num || "").replace(/\D+/g, "");
      if (!clean) {
        skipped++;
        continue;
      }
      next[code] = clean;
      applied++;
    }
    setContracts(next);
    setBulkOpen(false);
    setBulkText("");
    if (skipped > 0) {
      setError(`Applied ${applied}. Skipped ${skipped}.`);
      setTimeout(() => setError(null), 4000);
    }
  };

  const loading = profileLoading || productsLoading;

  return (
    <div className="space-y-6">
      {/* ─── KPI strip ─── */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <KpiTile
          icon={MapPin}
          label="State licenses"
          value={String(cleanLicenses.length)}
        />
        <KpiTile
          icon={ShieldCheck}
          label="Carrier appointments"
          value={String(filledContracts.length)}
        />
        <KpiTile
          icon={CalendarDays}
          label="Last updated"
          value={lastSyncedDisplay}
        />
      </div>

      {/* ─── User Information ─── */}
      <Section
        icon={UserIcon}
        title="User Information"
        right={
          <SaveStatus
            saving={saving}
            savedFlash={savedFlash}
            onSave={save}
            disabled={loading || !form.npn.trim() || !cleanLicenses.length}
          />
        }
      >
        {loading ? (
          <SectionLoader />
        ) : (
          <div className="space-y-3">
            <Row>
              <Field label="First Name">
                <Input
                  type="text"
                  value={form.firstName}
                  onChange={(e) =>
                    setForm((f) => ({ ...f, firstName: e.target.value }))
                  }
                  className="h-9"
                />
              </Field>
              <Field label="Last Name">
                <Input
                  type="text"
                  value={form.lastName}
                  onChange={(e) =>
                    setForm((f) => ({ ...f, lastName: e.target.value }))
                  }
                  className="h-9"
                />
              </Field>
            </Row>
            <Row>
              <Field label="Email" readOnly>
                <ReadOnly value={form.email} />
              </Field>
              <Field label="Phone">
                <Input
                  type="text"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  placeholder="(586) 551-6244"
                  value={form.phone}
                  onChange={(e) =>
                    setForm((f) => ({
                      ...f,
                      phone: e.target.value.replace(/\D+/g, ""),
                    }))
                  }
                  maxLength={11}
                  className="h-9"
                />
              </Field>
            </Row>
            <Row>
              <Field label="Date of Birth">
                <Input
                  type="date"
                  value={form.dob}
                  onChange={(e) =>
                    setForm((f) => ({ ...f, dob: e.target.value }))
                  }
                  max={new Date().toISOString().slice(0, 10)}
                  className="h-9"
                />
              </Field>
              <Field label="SSN">
                <Input
                  type="text"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  placeholder={
                    form.ssnLastFour ? `•••-••-${form.ssnLastFour}` : "9 digits"
                  }
                  value={ssnInput}
                  onChange={(e) =>
                    setSsnInput(e.target.value.replace(/\D+/g, ""))
                  }
                  maxLength={9}
                  className="h-9 font-mono tracking-widest"
                />
                <Hint>
                  <span className="inline-flex items-center gap-1">
                    <ShieldCheck className="h-3 w-3 text-emerald-600" />
                    {form.ssnLastFour
                      ? `Securely stored — ending in ${form.ssnLastFour}.`
                      : "Encrypted and stored securely."}
                  </span>
                </Hint>
              </Field>
            </Row>
            <Row>
              <Field label="NPN" required>
                <Input
                  type="text"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  value={form.npn}
                  onChange={(e) =>
                    setForm((f) => ({
                      ...f,
                      npn: e.target.value.replace(/\D+/g, ""),
                    }))
                  }
                  maxLength={15}
                  className="h-9 font-mono"
                />
              </Field>
              <Field label="NPN State" readOnly>
                <ReadOnly value={form.npnState || "—"} />
              </Field>
            </Row>
            <Field label="Address">
              <Input
                type="text"
                value={form.address}
                onChange={(e) =>
                  setForm((f) => ({ ...f, address: e.target.value }))
                }
                maxLength={255}
                className="h-9"
              />
            </Field>
            <div className="grid grid-cols-[1fr_120px_120px] gap-3">
              <Field label="City">
                <Input
                  type="text"
                  value={form.city}
                  onChange={(e) =>
                    setForm((f) => ({ ...f, city: e.target.value }))
                  }
                  maxLength={100}
                  className="h-9"
                />
              </Field>
              <Field label="State">
                <select
                  value={form.state}
                  onChange={(e) =>
                    setForm((f) => ({ ...f, state: e.target.value }))
                  }
                  className="h-9 w-full rounded-md border border-gray-200 bg-white px-2 text-sm focus:border-primary focus:outline-none"
                >
                  <option value="">—</option>
                  {US_STATES.map((s) => (
                    <option key={s} value={s}>
                      {s}
                    </option>
                  ))}
                </select>
              </Field>
              <Field label="Zip">
                <Input
                  type="text"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  value={form.zip}
                  onChange={(e) =>
                    setForm((f) => ({
                      ...f,
                      zip: e.target.value.replace(/\D+/g, ""),
                    }))
                  }
                  maxLength={10}
                  className="h-9 font-mono"
                />
              </Field>
            </div>
          </div>
        )}
      </Section>

      {/* ─── State Licenses ─── */}
      <div ref={licensesRef} className="scroll-mt-4">
      <Section icon={Building2} title="State Licenses">
        <div className="space-y-2">
          {licenses.map((l, i) => (
            <div key={i} className="flex items-center gap-2">
              <select
                value={l.stateCode}
                onChange={(e) =>
                  setLicenses((arr) => {
                    const next = [...arr];
                    next[i] = { ...next[i], stateCode: e.target.value };
                    return next;
                  })
                }
                className="h-9 w-24 rounded-md border border-gray-200 bg-white px-2 text-sm focus:border-primary focus:outline-none"
              >
                <option value="">State</option>
                {US_STATES.map((s) => (
                  <option key={s} value={s}>
                    {s}
                  </option>
                ))}
              </select>
              <Input
                type="text"
                inputMode="numeric"
                pattern="[0-9]*"
                placeholder="License number"
                value={l.licenseNumber}
                onChange={(e) =>
                  setLicenses((arr) => {
                    const next = [...arr];
                    next[i] = {
                      ...next[i],
                      licenseNumber: e.target.value.replace(/\D+/g, ""),
                    };
                    return next;
                  })
                }
                data-state={l.stateCode}
                className={`flex-1 h-9 font-mono text-sm ${
                  focusStateCode &&
                  l.stateCode === focusStateCode.toUpperCase() &&
                  !l.licenseNumber
                    ? "border-amber-400 ring-2 ring-amber-200"
                    : ""
                }`}
                maxLength={32}
              />
              <button
                type="button"
                onClick={() =>
                  setLicenses((arr) => {
                    const next = arr.filter((_, idx) => idx !== i);
                    return next.length ? next : [{ stateCode: "", licenseNumber: "" }];
                  })
                }
                className="flex-shrink-0 h-9 w-9 rounded-md hover:bg-gray-50 text-gray-400 hover:text-gray-600 flex items-center justify-center"
                aria-label="Remove"
              >
                <Trash2 className="h-3.5 w-3.5" />
              </button>
            </div>
          ))}
        </div>
        <Button
          type="button"
          variant="outline"
          size="sm"
          className="mt-3"
          onClick={() =>
            setLicenses((arr) => [...arr, { stateCode: "", licenseNumber: "" }])
          }
        >
          <Plus className="h-3.5 w-3.5 mr-1.5" />
          Add state
        </Button>
      </Section>
      </div>

      {/* ─── Carrier Appointments ─── */}
      <Section
        icon={Layers}
        title="Carrier Appointments"
        right={
          <div className="flex items-center gap-2">
            <span className="text-[11px] text-gray-500 font-medium bg-gray-100 px-2 py-1 rounded">
              {filledContracts.length}/{carriers.length}
            </span>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={async () => {
                setForceRefresh(true);
                await refetchProducts();
                setTimeout(() => setForceRefresh(false), 500);
              }}
              disabled={productsRefreshing && forceRefresh}
              className="h-8 w-8 p-0"
              title="Pull the latest carrier list"
            >
              <RefreshCw
                className={`h-3.5 w-3.5 ${
                  productsRefreshing && forceRefresh ? "animate-spin" : ""
                }`}
              />
            </Button>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => setBulkOpen(true)}
            >
              <Clipboard className="h-3.5 w-3.5 mr-1.5" />
              Paste list
            </Button>
          </div>
        }
      >
        {productsLoading ? (
          <SectionLoader />
        ) : (
          <div className="border border-gray-100 rounded-md divide-y divide-gray-100 max-h-[400px] overflow-y-auto">
            {carriers.map((c) => {
              const val = contracts[c.carrierCode] || "";
              const filled = !!val.trim();
              const rule = ruleForCarrier(c.carrierCode);
              const err = filled
                ? validateWritingNumber(c.carrierCode, val)
                : null;
              return (
                <div
                  key={c.carrierCode}
                  className="flex items-center gap-3 px-3 py-2.5 hover:bg-gray-50/50 transition-colors"
                >
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-medium text-gray-900 truncate">
                      {c.carrierName}
                    </p>
                    {err ? (
                      <p className="text-[11px] text-amber-600 mt-0.5">{err}</p>
                    ) : rule.hint ? (
                      <p className="text-[11px] text-gray-400 mt-0.5">
                        {rule.hint}
                      </p>
                    ) : null}
                  </div>
                  <Input
                    type="text"
                    placeholder="Writing #"
                    value={val}
                    onChange={(e) =>
                      setContracts({
                        ...contracts,
                        [c.carrierCode]: sanitizeWritingNumberInput(
                          c.carrierCode,
                          e.target.value,
                        ),
                      })
                    }
                    className={`w-40 h-8 font-mono text-sm ${
                      err ? "border-amber-300 focus-visible:ring-amber-300" : ""
                    }`}
                    maxLength={rule.exact ?? rule.max ?? 20}
                  />
                  <span className="w-4 flex-shrink-0 flex items-center justify-center">
                    {filled && !err && (
                      <Check className="h-3.5 w-3.5 text-emerald-500" />
                    )}
                  </span>
                </div>
              );
            })}
          </div>
        )}
      </Section>

      {/* ─── Sticky save / close footer ─── */}
      <div className="flex items-center justify-between pt-2 border-t border-gray-100">
        {onClose ? (
          <Button type="button" variant="ghost" onClick={onClose}>
            Close
          </Button>
        ) : (
          <span />
        )}
        <Button
          type="button"
          onClick={save}
          disabled={saving || loading}
          className="min-w-[140px]"
        >
          {saving ? (
            <>
              <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />
              Saving…
            </>
          ) : savedFlash ? (
            <>
              <Check className="h-3.5 w-3.5 mr-1.5" />
              Saved
            </>
          ) : (
            "Save changes"
          )}
        </Button>
      </div>

      {error && (
        <div className="flex items-start gap-2 rounded-md bg-rose-50 border border-rose-200 px-3 py-2 text-xs text-rose-700">
          <AlertCircle className="h-3.5 w-3.5 flex-shrink-0 mt-0.5" />
          <span>{error}</span>
        </div>
      )}

      {/* Carrier bulk-paste modal */}
      {bulkOpen && (
        <BulkPasteModal
          value={bulkText}
          setValue={setBulkText}
          carriers={carriers}
          onApply={applyBulk}
          onClose={() => setBulkOpen(false)}
        />
      )}
    </div>
  );
}

// ─────────────────────────── small primitives ───────────────────────────

interface EditableProfile {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  npn: string;
  npnState: string;
  dob: string;
  address: string;
  city: string;
  state: string;
  zip: string;
  ssnLastFour: string;
}

function emptyForm(): EditableProfile {
  return {
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    npn: "",
    npnState: "",
    dob: "",
    address: "",
    city: "",
    state: "",
    zip: "",
    ssnLastFour: "",
  };
}

function profileToEditable(p: ProducerProfileResponse): EditableProfile {
  return {
    firstName: p.firstName || "",
    lastName: p.lastName || "",
    email: p.email || "",
    phone: (p.phone || "").replace(/\D+/g, "").slice(-10),
    npn: p.npn || "",
    npnState: p.npnState || "",
    dob: p.dob || "",
    address: p.address || "",
    city: p.city || "",
    state: p.state || "",
    zip: p.zip || "",
    ssnLastFour: p.ssnLastFour || "",
  };
}

function KpiTile({
  icon: Icon,
  label,
  value,
}: {
  icon: React.ComponentType<{ className?: string }>;
  label: string;
  value: string;
}) {
  return (
    <div className="rounded-lg border border-gray-200 bg-white p-4 flex items-start gap-3">
      <span className="inline-flex h-9 w-9 items-center justify-center rounded-lg bg-primary/10 text-primary flex-shrink-0">
        <Icon className="h-4 w-4" />
      </span>
      <div className="min-w-0">
        <p className="text-[11px] uppercase tracking-wide text-gray-500 font-semibold">
          {label}
        </p>
        <p className="text-xl font-semibold text-gray-900 tabular-nums leading-tight mt-0.5">
          {value}
        </p>
      </div>
    </div>
  );
}

function Section({
  icon: Icon,
  title,
  right,
  children,
}: {
  icon: React.ComponentType<{ className?: string }>;
  title: string;
  right?: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <div className="rounded-lg border border-gray-200 bg-white">
      <div className="flex items-center justify-between gap-3 px-5 py-3 border-b border-gray-100">
        <h3 className="text-sm font-semibold text-gray-900 flex items-center gap-2">
          <Icon className="h-4 w-4 text-primary" />
          {title}
        </h3>
        {right}
      </div>
      <div className="p-5">{children}</div>
    </div>
  );
}

function SectionLoader() {
  return (
    <div className="py-8 flex items-center justify-center text-gray-400">
      <Loader2 className="h-4 w-4 animate-spin" />
    </div>
  );
}

function Row({ children }: { children: React.ReactNode }) {
  return <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">{children}</div>;
}

function Field({
  label,
  required,
  readOnly,
  children,
}: {
  label: string;
  required?: boolean;
  readOnly?: boolean;
  children: React.ReactNode;
}) {
  return (
    <label className="block">
      <span className="block text-[10px] uppercase tracking-wide text-gray-500 font-semibold mb-1">
        {label}
        {required && <span className="text-rose-500 ml-0.5">*</span>}
        {readOnly && <span className="text-gray-400 ml-1.5 normal-case font-normal">(read-only)</span>}
      </span>
      {children}
    </label>
  );
}

function ReadOnly({ value }: { value: string }) {
  return (
    <p className="text-sm text-gray-700 bg-gray-50 rounded-md px-3 py-2 border border-gray-100 h-9 flex items-center">
      {value || "—"}
    </p>
  );
}

function Hint({ children }: { children: React.ReactNode }) {
  return <p className="text-[10px] text-gray-500 mt-1">{children}</p>;
}

function SaveStatus({
  saving,
  savedFlash,
  onSave,
  disabled,
}: {
  saving: boolean;
  savedFlash: boolean;
  onSave: () => void;
  disabled: boolean;
}) {
  return (
    <Button
      type="button"
      size="sm"
      onClick={onSave}
      disabled={saving || disabled}
      className="h-8"
    >
      {saving ? (
        <>
          <Loader2 className="h-3 w-3 mr-1 animate-spin" />
          Saving…
        </>
      ) : savedFlash ? (
        <>
          <Check className="h-3 w-3 mr-1" />
          Saved
        </>
      ) : (
        "Save"
      )}
    </Button>
  );
}

function BulkPasteModal({
  value,
  setValue,
  carriers,
  onApply,
  onClose,
}: {
  value: string;
  setValue: (v: string) => void;
  carriers: Array<{ carrierCode: string; carrierName: string }>;
  onApply: () => void;
  onClose: () => void;
}) {
  // NESTED Radix dialog — the previous fixed-div (and later body-portal)
  // versions fought the parent dialog's transform/focus trap on the shell.
  // Radix nests dialogs natively: inner gets focus, outer pauses.
  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-xl">
        <DialogHeader>
          <DialogTitle>Paste writing numbers</DialogTitle>
        </DialogHeader>
        <p className="text-xs text-gray-500 -mt-2">
          One per line:{" "}
          <code className="bg-gray-100 px-1 rounded text-[11px]">
            carrier-code,writing-number
          </code>
        </p>
        <textarea
          value={value}
          onChange={(e) => setValue(e.target.value)}
          rows={8}
          placeholder={"sbli,123456\nlga,9100000000\nuhl,55501234"}
          className="w-full rounded-md border border-gray-200 px-3 py-2 font-mono text-sm focus:border-primary focus:outline-none"
        />
        <details className="mt-2 text-xs text-gray-500">
          <summary className="cursor-pointer font-medium">
            Carrier codes ({carriers.length})
          </summary>
          <div className="mt-2 flex flex-wrap gap-1">
            {carriers.map((c) => (
              <code
                key={c.carrierCode}
                className="bg-gray-100 px-1.5 py-0.5 rounded text-[10px]"
                title={c.carrierName}
              >
                {c.carrierCode}
              </code>
            ))}
          </div>
        </details>
        <div className="flex items-center justify-end gap-2 mt-4">
          <Button type="button" variant="ghost" onClick={onClose} size="sm">
            Cancel
          </Button>
          <Button type="button" onClick={onApply} size="sm">
            Apply
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default ManageProfile;
